import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, Plus, Rocket, Trash2, ExternalLink, Sparkles, TrendingUp, Zap, QrCode, CreditCard, BarChart3, Clock, Pencil, Wallet, Pause, Play, Video } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PixPaymentDialog from "@/components/PixPaymentDialog";
import CardPaymentDialog from "@/components/CardPaymentDialog";
import FullscreenImageViewer from "@/components/FullscreenImageViewer";
import AdFormDialog from "@/components/ads/AdFormDialog";
import AdAnalyticsDialog from "@/components/ads/AdAnalyticsDialog";
import AdsSpendingPanel from "@/components/ads/AdsSpendingPanel";
import AdsResultsTab from "@/components/ads/AdsResultsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Thumbnail for an ad: video (muted, autoplays on hover) or image. */
export function AdMedia({ ad, size = "w-24 h-24", onZoom, testid }) {
  if (ad.video_url) {
    return (
      <div className={`${size} rounded-xl overflow-hidden bg-black relative flex-shrink-0`} data-testid={testid}>
        <video src={ad.video_url} className="w-full h-full object-cover" muted loop playsInline preload="metadata"
          onMouseEnter={(e) => e.currentTarget.play().catch(() => {})} onMouseLeave={(e) => e.currentTarget.pause()} />
        <span className="absolute bottom-1 left-1 bg-black/60 text-white rounded-full p-1"><Video className="w-3 h-3" /></span>
      </div>
    );
  }
  if (!ad.image_url) return null;
  return (
    <button type="button" onClick={() => onZoom?.(ad.image_url)} data-testid={testid} className="flex-shrink-0" aria-label="Ampliar banner">
      <img src={ad.image_url} alt={ad.title} className={`${size} rounded-xl object-cover hover:opacity-90 transition-opacity cursor-zoom-in`} />
    </button>
  );
}

function BudgetBadge({ ad }) {
  if (!ad.budget_type || ad.budget_type === "none") return null;
  const label = ad.budget_type === "daily" ? `${BRL(ad.daily_budget)}/dia` : `${BRL(ad.total_budget)} total`;
  const cls = ad.budget_live ? "bg-emerald-500" : ad.budget_status === "completed" ? "bg-slate-500" : "bg-amber-500";
  const st = ad.budget_live ? "Veiculando" : ad.budget_status === "completed" ? "Concluído" : ad.budget_status === "paused" ? "Pausado" : "Limite do dia";
  return (
    <Badge className={`${cls} text-white text-[10px] gap-1`} title={ad.paused_reason || ""} data-testid={`my-ad-budget-${ad.ad_id}`}>
      <Wallet className="w-3 h-3" /> {label} · {st}
    </Badge>
  );
}

export default function Ads() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [mine, setMine] = useState([]);
  const [packages, setPackages] = useState([]);
  const [spending, setSpending] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [boosting, setBoosting] = useState(null);
  const [pixBoost, setPixBoost] = useState(null);
  const [cardBoost, setCardBoost] = useState(null);
  const [walletPaying, setWalletPaying] = useState(false);
  const [analyticsAd, setAnalyticsAd] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);

  const load = useCallback(async () => {
    const [f, m, p, s] = await Promise.all([
      axios.get(`${API}/ads`),
      axios.get(`${API}/ads/mine`),
      axios.get(`${API}/ads/boost-packages`),
      axios.get(`${API}/ads/spending`).catch(() => ({ data: null })),
    ]);
    setFeed(f.data); setMine(m.data); setPackages(p.data); setSpending(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Excluir anúncio?")) return;
    await axios.delete(`${API}/ads/${id}`);
    toast.success("Removido");
    load();
  };

  const toggleBudget = async (a) => {
    const next = a.budget_status === "paused" || a.budget_status === "completed" ? "active" : "paused";
    try {
      await axios.patch(`${API}/ads/${a.ad_id}`, { budget_status: next });
      toast.success(next === "active" ? "Veiculação retomada" : "Veiculação pausada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const payBoostWithWallet = async (pkg) => {
    const adId = boosting.ad_id;
    setWalletPaying(true);
    try {
      const r = await axios.post(`${API}/ads/${adId}/boost/wallet`, { package_id: pkg.package_id });
      toast.success(`Boost ativado! Saldo restante: ${BRL(r.data.wallet_balance)}`);
      setBoosting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao pagar com saldo");
    } finally { setWalletPaying(false); }
  };

  const trackImpression = async (adId) => {
    try { await axios.post(`${API}/ads/${adId}/impression`); }
    catch (err) { console.debug("[Ads] impression tracking failed", err?.response?.status); }
  };
  const trackClick = async (adId) => {
    try { await axios.post(`${API}/ads/${adId}/click`); }
    catch (err) { console.debug("[Ads] click tracking failed", err?.response?.status); }
  };

  const walletBalance = spending?.wallet_balance ?? 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Anúncios</h1>
            <p className="text-sm text-muted-foreground">Publique, alcance e impulsione</p>
          </div>
        </div>
        <Button data-testid="create-ad-btn" className="rounded-full gap-2" onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="w-4 h-4" /> Criar anúncio
        </Button>
      </div>

      <AdFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} isAdmin={!!user?.is_admin}
        cpm={spending?.cpm_brl || 19.9} walletBalance={walletBalance} onSaved={load} />

      <AdsSpendingPanel data={spending} />

      <Tabs defaultValue="feed">
        <TabsList className="rounded-full">
          <TabsTrigger value="feed" data-testid="ads-tab-feed" className="rounded-full">Descobrir</TabsTrigger>
          <TabsTrigger value="mine" data-testid="ads-tab-mine" className="rounded-full">Meus anúncios ({mine.length})</TabsTrigger>
          <TabsTrigger value="results" data-testid="ads-tab-results" className="rounded-full gap-1"><BarChart3 className="w-3.5 h-3.5" /> Resultados</TabsTrigger>
        </TabsList>

        <TabsContent value="feed" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {feed.length === 0 && (
              <Card className="p-10 text-center col-span-full text-muted-foreground">
                <Megaphone className="w-12 h-12 mx-auto opacity-30 mb-3" />
                <p>Nenhum anúncio ainda. Seja o primeiro!</p>
              </Card>
            )}
            {feed.map((a) => (
              <Card key={a.ad_id} data-testid={`ad-${a.ad_id}`} className="p-5 hover-lift flex gap-4 border-primary/40 bg-primary/5 relative overflow-hidden" onMouseEnter={() => trackImpression(a.ad_id)}>
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-bl-lg flex items-center gap-1" data-testid={`ad-sponsored-tag-${a.ad_id}`}>
                  <Sparkles className="w-3 h-3" /> Patrocinado
                </div>
                <AdMedia ad={a} onZoom={setZoomSrc} testid={`ad-zoom-${a.ad_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap pr-24">
                    {a.sponsored && <Badge className="bg-orange-500 text-[10px] gap-1"><Rocket className="w-3 h-3" /> Impulsionado</Badge>}
                    <span className="text-xs text-muted-foreground">por {a.author_name}</span>
                  </div>
                  <h3 className="font-bold tracking-tight leading-tight">{a.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{a.description}</p>
                  {a.target_url && (
                    <a href={a.target_url} target="_blank" rel="noopener noreferrer" onClick={() => trackClick(a.ad_id)} className="inline-flex items-center gap-1 text-sm text-primary font-semibold mt-2 hover:underline">
                      {a.cta || "Saiba mais"} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="mine" className="mt-6">
          <div className="space-y-4">
            {mine.length === 0 && <Card className="p-10 text-center text-muted-foreground">Você ainda não criou anúncios</Card>}
            {mine.map((a) => {
              const isBoosted = a.boost_expires_at && a.boost_expires_at > new Date().toISOString() && (a.boost_impressions_left || 0) > 0;
              const modStatus = a.moderation_status || "approved";
              const hasTargeting = (a.target_cities?.length || 0) + (a.target_states?.length || 0) + (a.target_countries?.length || 0) + (a.target_genders?.length || 0) + (a.target_interests?.length || 0) > 0;
              const hasBudget = a.budget_type && a.budget_type !== "none";
              return (
                <Card key={a.ad_id} className="p-5" data-testid={`my-ad-${a.ad_id}`}>
                  <div className="flex gap-4 flex-col sm:flex-row">
                    <AdMedia ad={a} size="w-20 h-20" onZoom={setZoomSrc} testid={`my-ad-zoom-${a.ad_id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold">{a.title}</h3>
                        {modStatus === "pending" && <Badge className="bg-amber-500 text-white text-[10px] gap-1" data-testid={`my-ad-status-pending-${a.ad_id}`}><Clock className="w-3 h-3" /> Em moderação</Badge>}
                        {modStatus === "rejected" && <Badge className="bg-red-500 text-white text-[10px]" data-testid={`my-ad-status-rejected-${a.ad_id}`}>Rejeitado</Badge>}
                        {modStatus === "approved" && <Badge className="bg-emerald-500 text-white text-[10px]" data-testid={`my-ad-status-approved-${a.ad_id}`}>Aprovado</Badge>}
                        {isBoosted && <Badge className="bg-primary"><Sparkles className="w-3 h-3 mr-1" />Impulsionado</Badge>}
                        <BudgetBadge ad={a} />
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{a.description}</p>
                      <div className="flex gap-4 text-xs text-muted-foreground mt-2 flex-wrap">
                        <span><TrendingUp className="w-3 h-3 inline mr-1" />{a.impressions || 0} impressões</span>
                        <span>{a.clicks || 0} cliques</span>
                        {hasBudget && <span data-testid={`my-ad-spent-${a.ad_id}`}>Gasto: <b>{BRL(a.spent_brl)}</b></span>}
                        {isBoosted && <span className="text-primary font-semibold">{a.boost_impressions_left} impressões restantes</span>}
                      </div>
                      {hasTargeting && (
                        <div className="flex gap-1.5 mt-2 flex-wrap" data-testid={`my-ad-targeting-${a.ad_id}`}>
                          {(a.target_countries || []).map((c) => <Badge key={`co-${c}`} variant="outline" className="text-[10px]">🌎 {c}</Badge>)}
                          {(a.target_states || []).map((c) => <Badge key={`s-${c}`} variant="outline" className="text-[10px]">🗺️ {c}</Badge>)}
                          {(a.target_cities || []).map((c) => <Badge key={`c-${c}`} variant="outline" className="text-[10px]">📍 {c}</Badge>)}
                          {(a.target_genders || []).map((g) => <Badge key={`g-${g}`} variant="outline" className="text-[10px]">{g === "male" ? "♂ Masculino" : g === "female" ? "♀ Feminino" : "Outro"}</Badge>)}
                          {(a.target_interests || []).map((i) => <Badge key={`i-${i}`} variant="outline" className="text-[10px]"># {i}</Badge>)}
                        </div>
                      )}
                    </div>
                    <div className="flex sm:flex-col gap-1 flex-wrap">
                      <Button data-testid={`boost-btn-${a.ad_id}`} size="sm" className="rounded-full gap-1" onClick={() => setBoosting(a)} disabled={modStatus !== "approved"}>
                        <Rocket className="w-4 h-4" /> Impulsionar
                      </Button>
                      <Button data-testid={`analytics-btn-${a.ad_id}`} size="sm" variant="outline" className="rounded-full gap-1" onClick={() => setAnalyticsAd(a)}>
                        <BarChart3 className="w-4 h-4" /> Analytics
                      </Button>
                      <Button data-testid={`edit-ad-${a.ad_id}`} size="sm" variant="outline" className="rounded-full gap-1" onClick={() => { setEditing(a); setFormOpen(true); }}>
                        <Pencil className="w-4 h-4" /> Editar
                      </Button>
                      {hasBudget && (
                        <Button data-testid={`budget-toggle-${a.ad_id}`} size="sm" variant="ghost" className="rounded-full gap-1" onClick={() => toggleBudget(a)}>
                          {a.budget_status === "active" ? <><Pause className="w-4 h-4" /> Pausar</> : <><Play className="w-4 h-4" /> Retomar</>}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => remove(a.ad_id)} data-testid={`delete-ad-${a.ad_id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="results" className="mt-6">
          <AdsResultsTab onOpenAd={(row) => setAnalyticsAd(mine.find((m) => m.ad_id === row.ad_id) || row)} />
        </TabsContent>
      </Tabs>

      {/* Boost dialog */}
      <Dialog open={!!boosting} onOpenChange={(v) => !v && setBoosting(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Impulsionar "{boosting?.title}"</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between text-xs text-muted-foreground -mt-1" data-testid="boost-wallet-balance">
            <span className="inline-flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Saldo da carteira: <b className="text-foreground">{BRL(walletBalance)}</b></span>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {packages.map((p) => {
              const canPay = walletBalance >= p.price_brl;
              return (
                <Card key={p.package_id} data-testid={`boost-pkg-${p.package_id}`} className="p-4 border-2 hover:border-primary transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-3"><Rocket className="w-4 h-4 text-primary" /></div>
                  <h4 className="font-bold">{p.name}</h4>
                  <div className="text-2xl font-bold text-primary my-2">R$ {p.price_brl.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground space-y-1 mb-3">
                    <div><Zap className="w-3 h-3 inline mr-1" />{p.impressions.toLocaleString("pt-BR")} impressões</div>
                    <div>{p.days} dias</div>
                  </div>
                  <div className="space-y-1.5">
                    <Button data-testid={`boost-pkg-wallet-${p.package_id}`} onClick={() => payBoostWithWallet(p)} disabled={walletPaying || !canPay}
                      title={canPay ? "" : "Saldo insuficiente"} className="w-full rounded-full h-9 gap-1 text-xs">
                      <Wallet className="w-3.5 h-3.5" /> {canPay ? "Pagar com saldo" : "Saldo insuficiente"}
                    </Button>
                    <Button data-testid={`boost-pkg-pix-${p.package_id}`} onClick={() => { const adId = boosting.ad_id; setBoosting(null); setPixBoost({ ad_id: adId, package_id: p.package_id, name: p.name, amount: p.price_brl }); }}
                      className="w-full rounded-full h-9 gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
                      <QrCode className="w-3.5 h-3.5" /> PIX
                    </Button>
                    <Button data-testid={`boost-pkg-card-${p.package_id}`} onClick={() => { const adId = boosting.ad_id; setBoosting(null); setCardBoost({ ad_id: adId, package_id: p.package_id, name: p.name, amount: p.price_brl }); }}
                      variant="outline" className="w-full rounded-full h-9 gap-1 text-xs">
                      <CreditCard className="w-3.5 h-3.5" /> Cartão (até 12x)
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Pague com o saldo da carteira (ativa na hora), PIX (admin confirma em minutos) ou cartão de crédito (Pagar.me, ativa na hora)
          </p>
        </DialogContent>
      </Dialog>

      <CardPaymentDialog open={!!cardBoost} onOpenChange={(v) => !v && setCardBoost(null)} purpose="ad_boost" amount={cardBoost?.amount || 0}
        referenceId={cardBoost?.ad_id} packageId={cardBoost?.package_id} title={cardBoost ? `Impulsionar — pacote ${cardBoost.name}` : "Impulsionar anúncio"}
        description="O boost é ativado automaticamente assim que o pagamento for aprovado." onSuccess={() => { toast.success("Boost ativado!"); load(); }} />

      <PixPaymentDialog open={!!pixBoost} onOpenChange={(v) => !v && setPixBoost(null)} purpose="ad_boost" amount={pixBoost?.amount || 0}
        refId={pixBoost?.ad_id} note={pixBoost ? `Boost pacote ${pixBoost.name}` : ""} />

      <AdAnalyticsDialog ad={analyticsAd} onClose={() => setAnalyticsAd(null)} />
      <FullscreenImageViewer src={zoomSrc} onClose={() => setZoomSrc(null)} />
    </div>
  );
}
